import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Sun, Video, CalendarDays, MapPin, Phone, Mail } from "lucide-react";
import api from "@/lib/api";

const HERO_FALLBACK =
  "https://images.unsplash.com/photo-1516117172878-fd2c41f4a759?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwxfHxzbW9reSUyMG1vdW50YWlucyUyMHN1bnJpc2UlMjBsYW5kc2NhcGV8ZW58MHx8fHwxNzgzOTA0NjQ2fDA&ixlib=rb-4.1.0&q=85";

const TPL = {
  modern: { heading: "font-display font-bold", hero: "items-start text-left", overlay: "bg-slate-900/55", btn: "rounded-full", cta: "Join Us Sunday" },
  traditional: { heading: "font-display font-semibold italic", hero: "items-center text-center", overlay: "bg-slate-900/65", btn: "rounded-none border border-white/70", cta: "You Are Welcome Here" },
  youth: { heading: "font-body font-extrabold uppercase tracking-tight", hero: "items-center text-center", overlay: "bg-slate-900/45", btn: "rounded-full uppercase tracking-wide", cta: "Come Hang With Us" },
};

export default function PublicSite() {
  const { churchId } = useParams();
  const [content, setContent] = useState(undefined);
  const [page, setPage] = useState("home");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/site/${churchId}`);
        setContent(data.content);
      } catch {
        setContent(null);
      }
    })();
  }, [churchId]);

  if (content === undefined)
    return <div className="min-h-screen grid place-items-center bg-[#FAFAFA]"><div className="h-10 w-10 rounded-full border-2 border-amber-600 border-t-transparent animate-spin" /></div>;

  if (content === null)
    return (
      <div className="min-h-screen grid place-items-center bg-[#FAFAFA] font-body text-center px-6" data-testid="site-not-published">
        <div>
          <span className="grid place-items-center h-12 w-12 rounded-full bg-amber-50 text-amber-600 mx-auto"><Sun className="h-6 w-6" /></span>
          <h1 className="mt-5 font-display text-2xl font-bold text-slate-900">Website not published yet</h1>
          <p className="mt-2 text-slate-600">This church's website hasn't been published.</p>
          <Link to="/" className="mt-6 inline-block text-amber-700 font-semibold hover:underline">Back to TN Web Design</Link>
        </div>
      </div>
    );

  const t = TPL[content.template] || TPL.modern;
  const accent = content.theme_color || "#D97706";
  const isMulti = content.site_type === "multi";

  const hero = (
    <section className="relative h-[70vh] min-h-[420px]">
      <img src={content.hero_image || HERO_FALLBACK} alt="Hero" className="h-full w-full object-cover" onError={(e) => { e.currentTarget.src = HERO_FALLBACK; }} />
      <div className={`absolute inset-0 ${t.overlay}`} />
      <div className={`absolute inset-0 flex flex-col justify-center px-8 md:px-20 text-white ${t.hero}`}>
        <h1 className={`${t.heading} text-4xl md:text-6xl drop-shadow`}>{content.church_name || "Church Name"}</h1>
        {content.tagline && <p className="mt-4 text-lg text-slate-100 max-w-xl">{content.tagline}</p>}
        <span className={`mt-6 px-6 py-3 text-sm font-semibold text-white ${t.btn}`} style={{ backgroundColor: accent }}>{t.cta}</span>
      </div>
    </section>
  );

  const aboutSection = content.about && (
    <section data-testid="section-about">
      <h2 className="font-display text-3xl font-bold" style={{ color: accent }}>About Us</h2>
      <p className="mt-4 text-slate-700 leading-relaxed whitespace-pre-line max-w-3xl">{content.about}</p>
    </section>
  );

  const infoSection = (
    <section className="grid sm:grid-cols-2 gap-6">
      <div className="rounded-2xl bg-slate-50 p-6">
        <div className="font-display text-lg font-bold text-slate-900">Service Times</div>
        <p className="mt-1 text-slate-600">{content.service_times || "Sundays at 10 AM"}</p>
      </div>
      <div className="rounded-2xl bg-slate-50 p-6">
        <div className="font-display text-lg font-bold text-slate-900">Visit Us</div>
        <p className="mt-1 text-slate-600 inline-flex items-center gap-2"><MapPin className="h-4 w-4" />{content.address || "Our location"}</p>
      </div>
    </section>
  );

  const sermonsSection = content.sermons?.length > 0 && (
    <section data-testid="section-sermons">
      <h2 className="font-display text-3xl font-bold" style={{ color: accent }}>Recent Sermons</h2>
      <div className="mt-5 grid sm:grid-cols-2 gap-4">
        {content.sermons.map((s, i) => (
          <a key={i} href={s.link || "#"} target={s.link ? "_blank" : undefined} rel="noreferrer" className="rounded-xl border border-slate-200 p-5 hover:shadow-md transition-shadow duration-200 flex items-start gap-3">
            <Video className="h-5 w-5 shrink-0 mt-0.5" style={{ color: accent }} />
            <div>
              <div className="font-semibold text-slate-900">{s.title || "Sermon"}</div>
              <div className="text-sm text-slate-500">{[s.speaker, s.date].filter(Boolean).join(" · ")}</div>
            </div>
          </a>
        ))}
      </div>
    </section>
  );

  const eventsSection = content.events?.length > 0 && (
    <section data-testid="section-events">
      <h2 className="font-display text-3xl font-bold" style={{ color: accent }}>Upcoming Events</h2>
      <div className="mt-5 space-y-4">
        {content.events.map((ev, i) => (
          <div key={i} className="rounded-xl border border-slate-200 p-5 flex items-start gap-3">
            <CalendarDays className="h-5 w-5 shrink-0 mt-0.5" style={{ color: accent }} />
            <div>
              <div className="font-semibold text-slate-900">{ev.title || "Event"}</div>
              <div className="text-sm text-slate-500">{[ev.date, ev.location].filter(Boolean).join(" · ")}</div>
              {ev.description && <p className="mt-1 text-sm text-slate-600">{ev.description}</p>}
            </div>
          </div>
        ))}
      </div>
    </section>
  );

  const contactSection = (
    <section data-testid="section-contact">
      <h2 className="font-display text-3xl font-bold" style={{ color: accent }}>Visit Us</h2>
      <div className="mt-5 grid sm:grid-cols-2 gap-6">
        <div className="rounded-2xl bg-slate-50 p-6"><div className="font-display text-lg font-bold text-slate-900">Service Times</div><p className="mt-1 text-slate-600">{content.service_times || "Sundays at 10 AM"}</p></div>
        <div className="rounded-2xl bg-slate-50 p-6"><div className="font-display text-lg font-bold text-slate-900">Address</div><p className="mt-1 text-slate-600 inline-flex items-center gap-2"><MapPin className="h-4 w-4" />{content.address || "Our location"}</p></div>
        {content.phone && <div className="rounded-2xl bg-slate-50 p-6"><div className="font-display text-lg font-bold text-slate-900">Phone</div><p className="mt-1 text-slate-600 inline-flex items-center gap-2"><Phone className="h-4 w-4" />{content.phone}</p></div>}
        {content.email && <div className="rounded-2xl bg-slate-50 p-6"><div className="font-display text-lg font-bold text-slate-900">Email</div><p className="mt-1 text-slate-600 inline-flex items-center gap-2"><Mail className="h-4 w-4" />{content.email}</p></div>}
      </div>
    </section>
  );

  const footer = (
    <footer className="bg-slate-900 text-slate-300 py-12 px-6 text-center">
      <div className="font-display text-xl font-bold text-white">{content.church_name}</div>
      <div className="mt-3 flex flex-wrap justify-center gap-6 text-sm">
        {content.phone && <span className="inline-flex items-center gap-2"><Phone className="h-4 w-4" />{content.phone}</span>}
        {content.email && <span className="inline-flex items-center gap-2"><Mail className="h-4 w-4" />{content.email}</span>}
      </div>
      <div className="mt-6 text-xs text-slate-500">Powered by TN Web Design · Built to bring His light to the world.</div>
    </footer>
  );

  if (isMulti) {
    const navPages = [{ id: "home", label: "Home" }];
    if (content.about) navPages.push({ id: "about", label: "About" });
    if (content.sermons?.length > 0) navPages.push({ id: "sermons", label: "Sermons" });
    if (content.events?.length > 0) navPages.push({ id: "events", label: "Events" });
    navPages.push({ id: "contact", label: "Contact" });
    const current = navPages.some((p) => p.id === page) ? page : "home";
    return (
      <div className="min-h-screen bg-white font-body flex flex-col" data-testid="public-site" data-site-type="multi">
        <header className="sticky top-0 z-20 bg-white/95 backdrop-blur border-b border-slate-200">
          <nav className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between gap-4 flex-wrap">
            <button onClick={() => setPage("home")} className="font-display text-lg font-bold text-slate-900">{content.church_name || "Our Church"}</button>
            <div className="flex items-center gap-5 flex-wrap">
              {navPages.map((p) => (
                <button
                  key={p.id}
                  data-testid={`page-nav-${p.id}`}
                  onClick={() => setPage(p.id)}
                  className={`text-sm font-semibold transition-colors duration-200 ${current === p.id ? "" : "text-slate-500 hover:text-slate-900"}`}
                  style={current === p.id ? { color: accent } : undefined}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </nav>
        </header>

        <main className="flex-1" data-testid={`page-${current}`}>
          {current === "home" && (<>{hero}<div className="max-w-5xl mx-auto px-6 py-16 space-y-12">{infoSection}</div></>)}
          {current === "about" && <div className="max-w-5xl mx-auto px-6 py-16">{aboutSection}</div>}
          {current === "sermons" && <div className="max-w-5xl mx-auto px-6 py-16">{sermonsSection}</div>}
          {current === "events" && <div className="max-w-5xl mx-auto px-6 py-16">{eventsSection}</div>}
          {current === "contact" && <div className="max-w-5xl mx-auto px-6 py-16">{contactSection}</div>}
        </main>
        {footer}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white font-body" data-testid="public-site" data-site-type="single">
      {hero}
      <div className="max-w-5xl mx-auto px-6 py-16 space-y-16">
        {aboutSection}
        {infoSection}
        {sermonsSection}
        {eventsSection}
      </div>
      {footer}
    </div>
  );
}
